<template>
  <AModal
    :model-value="modelValue"
    size="lg"
    @update:model-value="$emit('update:modelValue', $event)"
  >
    <template #title>
      <span class="apf__title">
        <i class="material-icons-round apf__title-icon">{{ isEdit ? 'edit_note' : 'add_box' }}</i>
        {{ isEdit ? 'AI 프로필 수정' : 'AI 프로필 등록' }}
      </span>
    </template>
    <template #sub>
      {{ isEdit
        ? '식별자와 서버 종류는 등록 후 바꿀 수 없습니다. 나머지는 저장하면 최대 30초 안에 반영됩니다.'
        : '등록만으로는 아무 기능도 이 프로필을 쓰지 않습니다. 등록 → 연결 테스트 → 기능에 지정 순서입니다.' }}
    </template>

    <div class="apf">
      <div class="apf__grid">
        <div class="p-field">
          <label class="p-field__label">
            <i class="material-icons-round p-field__label-icon">key</i>
            프로필 코드 <span v-if="!isEdit" class="p-field__req">*</span>
          </label>
          <input
            v-model.trim="form.profile_code"
            class="p-input apf__mono"
            :class="{ 'is-invalid': codeError }"
            type="text"
            :readonly="isEdit"
            maxlength="50"
            placeholder="예: azure_backup"
          />
          <div v-if="codeError" class="p-field__error">{{ codeError }}</div>
          <div v-else class="p-field__hint">
            <i>{{ isEdit ? 'lock' : 'info' }}</i>
            {{ isEdit
              ? '설정·로그에 그대로 남는 값이라 등록 후에는 바꿀 수 없습니다.'
              : '영문 소문자·숫자·밑줄만, 2~50자.' }}
          </div>
        </div>

        <div class="p-field">
          <label class="p-field__label">
            <i class="material-icons-round p-field__label-icon">dns</i>
            서버 종류 <span v-if="!isEdit" class="p-field__req">*</span>
          </label>
          <select v-model="form.provider_type" class="p-input" :disabled="isEdit" @change="onProviderChange">
            <option v-for="t in providerTypeOptions" :key="t.value" :value="t.value">{{ t.label }}</option>
          </select>
          <div class="p-field__hint">
            <i>{{ isEdit ? 'lock' : 'info' }}</i>
            {{ isEdit ? '등록 후에는 바꿀 수 없습니다.' : '고르면 주소 형태와 쓸 수 있는 키가 정해집니다.' }}
          </div>
        </div>

        <div class="p-field apf__span2">
          <label class="p-field__label">
            <i class="material-icons-round p-field__label-icon">link</i>
            서버 주소 <span v-if="form.is_active" class="p-field__req">*</span>
          </label>
          <input
            v-model.trim="form.base_url"
            class="p-input apf__mono"
            :class="{ 'is-invalid': urlError }"
            type="text"
            :placeholder="providerMeta.urlPlaceholder"
          />
          <div v-if="urlError" class="p-field__error">{{ urlError }}</div>
          <div class="p-field__hint"><i>info</i>{{ providerMeta.urlHint }}</div>
          <!-- 허용 도메인은 관리자가 주소를 고칠 때 바로 봐야 하는 값이라 오류와 무관하게 항상 띄웁니다. -->
          <ul v-if="form.provider_type === 'azure_openai'" class="apf__suffixes">
            <li v-for="s in AZURE_HOST_SUFFIXES" :key="s">{{ s }}</li>
          </ul>
        </div>

        <div class="p-field">
          <label class="p-field__label">
            <i class="material-icons-round p-field__label-icon">vpn_key</i>
            API 키 이름
          </label>
          <select v-model="form.api_key_env" class="p-input">
            <option value="">없음</option>
            <option v-for="env in keyEnvOptions" :key="env" :value="env">{{ env }}</option>
          </select>
          <!--
            키 "값" 을 입력하는 칸은 일부러 두지 않습니다. 값은 서버 설정에만 있고 API 로
            주고받지 않습니다(docs/AI설정_관리자화면_가이드.md §2-2). 화면은 어느 이름의 키를 쓸지만 고릅니다.
          -->
          <div class="p-field__hint">
            <i>lock</i>키 "값" 이 아니라 서버에 저장된 키의 "이름" 입니다.
          </div>
        </div>

        <div class="p-field">
          <label class="p-field__label">
            <i class="material-icons-round p-field__label-icon">smart_toy</i>
            기본 모델
          </label>
          <input
            v-model.trim="form.default_model"
            class="p-input apf__mono"
            type="text"
            :placeholder="form.provider_type === 'azure_openai' ? 'deployment 이름' : '비워 두면 서버가 선택'"
          />
          <div class="p-field__hint">
            <i>info</i>
            {{ form.provider_type === 'azure_openai'
              ? 'Azure 는 모델명이 아니라 deployment 이름을 넣습니다.'
              : 'vLLM 은 비워 두면 서버가 알아서 고릅니다.' }}
          </div>
        </div>

        <div v-if="form.provider_type === 'azure_openai'" class="p-field">
          <label class="p-field__label">
            <i class="material-icons-round p-field__label-icon">tag</i>
            API 버전
          </label>
          <input v-model.trim="form.opt_api_version" class="p-input apf__mono" type="text" placeholder="2024-10-21" />
          <div class="p-field__hint">
            <i>info</i>
            {{ isV1Endpoint
              ? '주소가 v1 형태(/openai/v1)라 비워 두셔도 됩니다. 넣어도 무시됩니다.'
              : 'Azure 전용 설정입니다. 이 주소 형태에서는 필요합니다.' }}
          </div>
        </div>

        <div class="p-field">
          <label class="p-field__label">
            <i class="material-icons-round p-field__label-icon">timer</i>
            연결 대기 시간 (초)
          </label>
          <input
            v-model="form.opt_connect_timeout_sec"
            class="p-input"
            type="number"
            min="1"
            max="30"
            placeholder="5"
          />
          <div v-if="optionError" class="p-field__error">{{ optionError }}</div>
          <div v-else class="p-field__hint"><i>info</i>1~30초, 기본 5초. 이 안에 연결이 안 되면 예비 프로필로 넘어갑니다.</div>
        </div>

        <div class="p-field">
          <label class="p-field__label">
            <i class="material-icons-round p-field__label-icon">data_object</i>
            토큰 파라미터 이름
          </label>
          <select v-model="form.opt_max_tokens_param" class="p-input">
            <option value="">기본값</option>
            <option v-for="p in MAX_TOKENS_PARAMS" :key="p" :value="p">{{ p }}</option>
          </select>
          <div class="p-field__hint"><i>info</i>일부 모델용. 보통 비워 둡니다.</div>
        </div>

        <div class="p-field apf__span2">
          <label class="p-field__label">
            <i class="material-icons-round p-field__label-icon">notes</i>
            설명
          </label>
          <input v-model.trim="form.description" class="p-input" type="text" maxlength="200" placeholder="예: 예비용 Azure" />
        </div>
      </div>

      <div class="apf__toggles">
        <div class="apf__toggle">
          <button
            type="button"
            :class="['p-switch', { 'is-on': form.is_cloud, 'is-disabled': cloudLocked }]"
            :disabled="cloudLocked"
            @click="!cloudLocked && (form.is_cloud = !form.is_cloud)"
          ></button>
          <div>
            <b>과금되는 외부 서비스</b>
            <span>
              {{ cloudLocked
                ? `${providerMeta.label} 은(는) 서버가 항상 과금으로 강제합니다. 끌 수 없습니다.`
                : '켜면 과금이 차단된 기능(학생 리포트 요약)에는 지정할 수 없게 됩니다.' }}
            </span>
          </div>
        </div>

        <div class="apf__toggle">
          <button
            type="button"
            :class="['p-switch', { 'is-on': form.is_active }]"
            @click="form.is_active = !form.is_active"
          ></button>
          <div>
            <b>사용</b>
            <!-- 끄기 전에 어디서 쓰이는지 미리 보여 줍니다(§2-2). 쓰이고 있으면 비활성화가 409 로 거부됩니다. -->
            <span v-if="usedBy.length"><b>{{ usedByLabel }}</b> 에서 사용 중이라 끌 수 없습니다.</span>
            <span v-else>끄면 기능 설정의 드롭다운에서 빠집니다. 삭제 기능은 없고 이 값으로 대신합니다.</span>
          </div>
        </div>
      </div>

      <!--
        used_by 가 비어 있지 않은 프로필의 비활성화는 서버가 409 로 거부합니다(§2-2).
        저장 버튼을 누르기 전에 미리 알려 왕복을 줄입니다.
      -->
      <div v-if="blockedByUsage" class="apf__alert apf__alert--warn">
        <i class="material-icons-round">report_problem</i>
        <span>
          <b>{{ usedByLabel }}</b> 에서 사용 중이라 비활성화할 수 없습니다.
          먼저 해당 기능의 1순위·예비 프로필을 다른 것으로 바꿔 주세요.
        </span>
      </div>

      <div v-if="isEdit && profile && profile.api_key_set === false && form.api_key_env" class="apf__alert apf__alert--err">
        <i class="material-icons-round">gpp_maybe</i>
        <span>
          <b>{{ form.api_key_env }} 키가 서버에 없습니다.</b>
          이 프로필은 호출해도 인증에 실패합니다. 화면에서 해결할 수 없고 개발자가 서버에 키를 넣고 배포해야 합니다.
        </span>
      </div>
    </div>

    <template #foot>
      <ABtn kind="ghost" @click="$emit('update:modelValue', false)">취소</ABtn>
      <ABtn kind="primary" icon="save" :loading="saving" :disabled="!canSave" @click="save">
        {{ isEdit ? '저장' : '등록' }}
      </ABtn>
    </template>
  </AModal>
</template>

<script>
import swal from "sweetalert2";
import AModal from "@/components/Pineapple/AModal.vue";
import ABtn from "@/components/Pineapple/ABtn.vue";
import { apiErrorMessage } from "@/utils/apiError";
import {
  PROVIDER_TYPES,
  MAX_TOKENS_PARAMS,
  AZURE_HOST_SUFFIXES,
  isCloudLocked,
  isAzureEndpoint,
  parseHttpUrl,
  providerLabel,
  featureLabel,
} from "@/utils/aiSettings";

const CODE_RE = /^[a-z0-9_]{2,50}$/;
const RESERVED_CODE = "bootstrap_vllm";

const emptyForm = () => ({
  profile_code: "",
  provider_type: "vllm",
  base_url: "",
  api_key_env: "",
  default_model: "",
  description: "",
  opt_api_version: "",
  opt_connect_timeout_sec: "",
  opt_max_tokens_param: "",
  is_cloud: false,
  is_active: true,
});

export default {
  name: "AiProfileFormModal",
  components: { AModal, ABtn },
  props: {
    modelValue: { type: Boolean, default: false },
    /** 수정 대상. null 이면 등록 폼입니다. */
    profile: { type: Object, default: null },
    /** 이미 등록된 프로필 코드 목록 — 중복 등록을 미리 막습니다. */
    existingCodes: { type: Array, default: () => [] },
    /** 서버가 내려준 키 이름 목록(GET /profiles 의 allowed_key_envs) */
    allowedKeyEnvs: { type: Array, default: () => [] },
    /** 서버가 내려준 서버 종류 목록(GET /profiles 의 provider_types) */
    providerTypes: { type: Array, default: () => [] },
  },
  emits: ["update:modelValue", "saved"],
  data() {
    return {
      form: emptyForm(),
      saving: false,
      MAX_TOKENS_PARAMS,
      AZURE_HOST_SUFFIXES,
    };
  },
  computed: {
    isEdit() {
      return !!this.profile;
    },
    providerMeta() {
      return PROVIDER_TYPES[this.form.provider_type] || {
        label: this.form.provider_type,
        urlHint: 'http(s) 로 시작하는 주소',
        urlPlaceholder: '',
      };
    },
    providerTypeOptions() {
      // 서버가 내려준 목록을 정본으로 쓰되, 표시명은 화면 표를 씁니다.
      const list = this.providerTypes.length ? this.providerTypes : Object.keys(PROVIDER_TYPES);
      return list.map((v) => ({ value: v, label: providerLabel(v) }));
    },
    /**
     * 키는 자기 종류의 프로필에만 쓸 수 있습니다(다른 종류를 고르면 422).
     * 종류에 맞는 이름 하나로 좁히되, 서버의 allowed_key_envs 에 없으면 내보내지 않습니다.
     */
    keyEnvOptions() {
      const want = this.providerMeta.keyEnv;
      if (!want) return [];
      if (this.allowedKeyEnvs.length && !this.allowedKeyEnvs.includes(want)) return [];
      return [want];
    },
    cloudLocked() {
      return isCloudLocked(this.form.provider_type);
    },
    usedBy() {
      return this.profile?.used_by || [];
    },
    usedByLabel() {
      return this.usedBy.map(featureLabel).join(", ");
    },
    /**
     * "이번 저장으로 비활성화하려는" 경우만 막습니다.
     * 이미 비활성인데 used_by 가 남아 있는 프로필(개발자가 API 로 직접 만든 상태 등)의
     * 주소 수정까지 막으면, 끄지도 않은 토글 때문에 저장이 안 되는 이유를 알 수 없습니다.
     */
    blockedByUsage() {
      return this.isEdit && this.usedBy.length > 0 && !this.form.is_active && !!this.profile?.is_active;
    },
    codeError() {
      if (this.isEdit) return "";
      const code = this.form.profile_code;
      if (!code) return "";
      if (code === RESERVED_CODE) return "예약어라 쓸 수 없는 코드입니다.";
      if (!CODE_RE.test(code)) return "영문 소문자·숫자·밑줄만, 2~50자로 입력하세요.";
      if (this.existingCodes.includes(code)) return "이미 등록된 프로필 코드입니다.";
      return "";
    },
    urlError() {
      const url = this.form.base_url;
      if (!url) {
        return this.form.is_active ? "사용(활성) 상태로 저장하려면 주소가 있어야 합니다." : "";
      }
      if (!parseHttpUrl(url)) return "http:// 또는 https:// 로 시작하는 올바른 주소여야 합니다.";
      // 서버와 같은 방식(호스트 접미사)으로 검사합니다. 문자열 포함으로 보면 운영 주소를 막고
      // 위장 주소를 통과시킵니다 — 근거는 utils/aiSettings.js 의 isAzureEndpoint 주석.
      if (this.form.provider_type === "azure_openai" && !isAzureEndpoint(url)) {
        return "Azure 리소스 주소가 아닙니다. https 이고 호스트가 아래 도메인 중 하나로 끝나야 합니다.";
      }
      return "";
    },
    /**
     * v1 형태 주소(`/openai/v1`)면 서버가 api_version 없이 호출합니다.
     * 서버가 주소로 갈라 부르므로 화면에서 구분할 것은 없고, 안내만 바꿉니다(docs/AI설정_관리자화면_가이드.md §2-5).
     */
    isV1Endpoint() {
      return /\/openai\/v1(\/|$)/.test(String(this.form.base_url || "").trim());
    },
    /** connect_timeout_sec 는 1~30 입니다. 범위를 넘기면 §1-4 의 영문 배열 422 가 옵니다. */
    optionError() {
      const v = this.form.opt_connect_timeout_sec;
      if (v === "" || v === null || v === undefined) return "";
      const n = Number(v);
      if (!Number.isInteger(n) || n < 1 || n > 30) return "연결 대기 시간은 1~30초 사이의 정수여야 합니다.";
      return "";
    },
    canSave() {
      if (this.saving) return false;
      if (this.codeError || this.urlError || this.optionError) return false;
      // 서버가 409 로 거부할 조합이라 눌러 봐야 실패합니다(§2-2).
      if (this.blockedByUsage) return false;
      if (!this.isEdit && !this.form.profile_code) return false;
      if (!this.form.provider_type) return false;
      return true;
    },
  },
  watch: {
    modelValue(open) {
      if (open) this.reset();
    },
  },
  methods: {
    reset() {
      const p = this.profile;
      if (!p) {
        this.form = emptyForm();
        return;
      }
      const opts = p.options || {};
      this.form = {
        profile_code: p.profile_code,
        provider_type: p.provider_type,
        base_url: p.base_url || "",
        api_key_env: p.api_key_env || "",
        default_model: p.default_model || "",
        description: p.description || "",
        opt_api_version: opts.api_version ?? "",
        opt_connect_timeout_sec: opts.connect_timeout_sec ?? "",
        opt_max_tokens_param: opts.max_tokens_param ?? "",
        is_cloud: !!p.is_cloud,
        is_active: !!p.is_active,
      };
    },

    /**
     * 종류를 바꾸면 그 종류에서 못 쓰는 값이 남지 않게 정리합니다.
     * 다른 종류의 키를 그대로 보내면 422 이고, api_version 은 Azure 전용입니다.
     */
    onProviderChange() {
      this.form.api_key_env = "";
      if (this.form.provider_type !== "azure_openai") this.form.opt_api_version = "";
      if (this.cloudLocked) this.form.is_cloud = true;
    },

    /**
     * options 는 키 단위 병합입니다. 비운 키는 null 로 보내야 지워집니다(§2-2).
     * 등록 때는 값이 있는 키만 담습니다.
     */
    buildOptions() {
      const prev = this.profile?.options || {};
      const next = {};
      const put = (key, value) => {
        const empty = value === "" || value === null || value === undefined;
        if (!empty) next[key] = value;
        else if (this.isEdit && key in prev) next[key] = null;
      };
      put("api_version", this.form.provider_type === "azure_openai" ? this.form.opt_api_version : "");
      const ct = this.form.opt_connect_timeout_sec;
      put("connect_timeout_sec", ct === "" || ct === null ? "" : Number(ct));
      put("max_tokens_param", this.form.opt_max_tokens_param);
      return next;
    },

    async save() {
      if (!this.canSave) return;
      this.saving = true;
      try {
        const options = this.buildOptions();
        const body = {
          base_url: this.form.base_url,
          // 빈 문자열을 보내면 서버가 값을 비웁니다(§2-2).
          api_key_env: this.form.api_key_env,
          default_model: this.form.default_model,
          description: this.form.description,
          is_cloud: this.cloudLocked ? true : this.form.is_cloud,
          is_active: this.form.is_active,
        };
        if (Object.keys(options).length) body.options = options;

        let res;
        if (this.isEdit) {
          res = await this.$axios({
            method: "put",
            url: `/system/ai-settings/profiles/${this.form.profile_code}`,
            data: body,
            loadingBar: true,
          });
        } else {
          // 등록 때는 안 채운 칸을 아예 빼서 보냅니다. 수정과 달리 "비우기" 의미가 필요 없고,
          // 서버 기본값(예: options 미지정)을 그대로 쓰게 하는 편이 안전합니다.
          const create = { profile_code: this.form.profile_code, provider_type: this.form.provider_type };
          Object.entries(body).forEach(([k, v]) => {
            if (v !== "" && v !== null && v !== undefined) create[k] = v;
          });
          res = await this.$axios({
            method: "post",
            url: "/system/ai-settings/profiles",
            data: create,
            loadingBar: true,
          });
        }

        const sec = res.data?.applies_within_sec;
        this.$notifyVue({
          type: "success",
          title: this.isEdit ? "프로필을 저장했습니다." : "프로필을 등록했습니다.",
          text: sec ? `실제 AI 호출에는 최대 ${sec}초 안에 반영됩니다.` : undefined,
        });
        this.$emit("saved");
        this.$emit("update:modelValue", false);
      } catch (e) {
        swal.fire("저장 실패", apiErrorMessage(e, "프로필을 저장하지 못했습니다."), "error");
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@import "@/assets/scss/pineapple";

.apf {
  display: flex;
  flex-direction: column;
  gap: 18px;

  &__title {
    display: inline-flex;
    align-items: center;
    gap: 8px;
  }

  &__title-icon {
    font-size: 20px !important;
    color: $p-point-deep;
  }

  &__grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 14px;

    @media (max-width: 768px) { grid-template-columns: 1fr; }
  }

  &__span2 {
    grid-column: 1 / -1;
  }

  &__mono {
    font-family: $p-font-mono;
    font-size: 12px;
  }

  &__suffixes {
    list-style: none;
    margin: 2px 0 0;
    padding: 0;
    display: flex;
    flex-wrap: wrap;
    gap: 4px 8px;

    > li {
      font-family: $p-font-mono;
      font-size: 10px;
      color: $p-ink-mid;
      background: $p-line-soft;
      border-radius: 4px;
      padding: 2px 6px;
    }
  }

  &__toggles {
    display: flex;
    flex-direction: column;
    gap: 12px;
    padding: 14px 16px;
    background: $p-panel-alt;
    border-radius: $p-radius-md;
  }

  &__toggle {
    display: flex;
    align-items: flex-start;
    gap: 12px;

    > div {
      display: flex;
      flex-direction: column;
      gap: 2px;
    }

    b {
      font-size: 13px;
      color: $p-ink;
    }

    span {
      font-size: 11px;
      color: $p-ink-mid;
      line-height: 1.5;
    }
  }

  &__alert {
    display: flex;
    align-items: flex-start;
    gap: 8px;
    padding: 12px 14px;
    border-radius: $p-radius-md;
    font-size: 12px;
    line-height: 1.6;

    > i { font-size: 18px; flex-shrink: 0; }

    &--warn { background: $p-warn-soft; border-left: 3px solid $p-warn; color: $p-warn-ink; }
    &--err  { background: $p-err-soft;  border-left: 3px solid $p-err;  color: $p-err-ink; }
  }
}
</style>
